import { lazy, Suspense } from 'react'
import { Navigate, Route, Routes } from 'react-router-dom'
import { ROUTES } from '@/routes/routes'
import { ProtectedRoute } from '@/components/auth/ProtectedRoute'
import { RequireHousehold } from '@/components/auth/RequireHousehold'
import { PendingSessionRecorder } from '@/components/auth/PendingSessionRecorder'
import { AnalyticsBootstrap } from '@/components/AnalyticsBootstrap'
import { AppLayout } from '@/components/layout/AppLayout'
import HomePage from '@/pages/HomePage'
import PrivacyPolicyPage from '@/pages/legal/PrivacyPolicyPage'
import TermsPage from '@/pages/legal/TermsPage'
import SignInPage from '@/pages/auth/SignInPage'
import SignUpPage from '@/pages/auth/SignUpPage'
import ForgotPasswordPage from '@/pages/auth/ForgotPasswordPage'
import DisavowSignupPage from '@/pages/auth/DisavowSignupPage'
import SSOCallbackPage from '@/pages/auth/SSOCallbackPage'
import OnboardingPage from '@/pages/app/OnboardingPage'
import AccountsPage from '@/pages/app/AccountsPage'
import LoansPage from '@/pages/app/LoansPage'
import LoanDetailPage from '@/pages/app/LoanDetailPage'
import PersonalLoanDetailPage from '@/pages/app/PersonalLoanDetailPage'
import DashboardPage from '@/pages/DashboardPage'
import EnableTotpPage from '@/pages/settings/EnableTotpPage'
import DisableTotpPage from '@/pages/settings/DisableTotpPage'
import SettingsPage from '@/pages/settings/SettingsPage'
import { TotpPageShell } from '@/components/settings/TotpPageShell'
import TransactionsPage from '@/pages/app/TransactionsPage'
import CalendarPage from '@/pages/app/CalendarPage'
import AcceptInvitePage from '@/pages/app/AcceptInvitePage'
import BudgetPage from '@/pages/app/BudgetPage'
import CategoriesPage from '@/pages/app/CategoriesPage'
import RecurringPage from '@/pages/app/RecurringPage'
import PeoplePage from '@/pages/app/PeoplePage'
import PersonDetailPage from '@/pages/app/PersonDetailPage'
import GoalsPage from '@/pages/app/GoalsPage'
import GoalDetailPage from '@/pages/app/GoalDetailPage'
import AccountDetailPage from '@/pages/app/AccountDetailPage'
import { MFA_ENABLED, PERSONAL_LOANS_ENABLED, SHARED_EXPENSES_ENABLED } from '@/lib/featureFlags'

// Analytics (and its charts) loads on demand: it would otherwise push the precached entry bundle
// past Workbox's 2 MiB limit.
const AnalyticsRoutes = lazy(() => import('@/pages/app/analytics/AnalyticsRoutes'))

export default function App() {
  return (
    <>
      <PendingSessionRecorder />
      <AnalyticsBootstrap />
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path={ROUTES.LEGAL.PRIVACY} element={<PrivacyPolicyPage />} />
        <Route path={ROUTES.LEGAL.TERMS} element={<TermsPage />} />
        <Route path={ROUTES.AUTH.SIGN_IN} element={<SignInPage />} />
        <Route path={ROUTES.AUTH.SIGN_UP} element={<SignUpPage />} />
        <Route path={ROUTES.AUTH.FORGOT_PASSWORD} element={<ForgotPasswordPage />} />
        <Route path={ROUTES.AUTH.DISAVOW_SIGNUP} element={<DisavowSignupPage />} />
        <Route path={ROUTES.AUTH.SSO_CALLBACK} element={<SSOCallbackPage />} />

        {/* Onboarding — requires auth but NOT a household */}
        <Route
          path={ROUTES.APP.ONBOARDING}
          element={
            <ProtectedRoute>
              <OnboardingPage />
            </ProtectedRoute>
          }
        />

        {/* Accept invite — public: not-yet-signed-in invitees land here too, and the page
            handles both cases itself (preview + sign-up/in links, or auto-accept once signed in). */}
        <Route path={ROUTES.APP.ACCEPT_INVITE} element={<AcceptInvitePage />} />

        {/* All /app/** except onboarding — require auth + household, wrapped in AppLayout */}
        <Route
          path="/app/*"
          element={
            <ProtectedRoute>
              <RequireHousehold>
                <AppLayout />
              </RequireHousehold>
            </ProtectedRoute>
          }
        >
          <Route path="dashboard" element={<DashboardPage />} />
          <Route path="accounts" element={<AccountsPage />} />
          <Route path="accounts/:id" element={<AccountDetailPage />} />
          <Route path="loans" element={<LoansPage />} />
          <Route path="loans/:id" element={<LoanDetailPage />} />
          {PERSONAL_LOANS_ENABLED && (
            <Route path="loans/people/:accountId" element={<PersonalLoanDetailPage />} />
          )}
          <Route path="transactions" element={<TransactionsPage />} />
          <Route path="calendar" element={<CalendarPage />} />
          <Route
            path="analytics/*"
            element={
              <Suspense fallback={<div className="p-6 md:p-10" aria-busy="true" />}>
                <AnalyticsRoutes />
              </Suspense>
            }
          />
          <Route path="budget" element={<BudgetPage />} />
          <Route path="categories" element={<CategoriesPage />} />
          <Route path="goals" element={<GoalsPage />} />
          <Route path="goals/:id" element={<GoalDetailPage />} />
          <Route path="recurring" element={<RecurringPage />} />
          {SHARED_EXPENSES_ENABLED && <Route path="people" element={<PeoplePage />} />}
          {SHARED_EXPENSES_ENABLED && <Route path="people/:id" element={<PersonDetailPage />} />}
          <Route path="settings" element={<SettingsPage />} />
          {/* The old one-tab-per-page Settings URLs, kept so bookmarks land on the right section. */}
          <Route
            path="settings/profile"
            element={<Navigate to={ROUTES.APP.SETTINGS_PROFILE} replace />}
          />
          <Route
            path="settings/security"
            element={<Navigate to={ROUTES.APP.SETTINGS_SECURITY} replace />}
          />
          <Route
            path="settings/household"
            element={<Navigate to={ROUTES.APP.SETTINGS_HOUSEHOLD} replace />}
          />
          <Route
            path="settings/members"
            element={<Navigate to={ROUTES.APP.SETTINGS_MEMBERS} replace />}
          />
          <Route
            path="settings/export"
            element={<Navigate to={ROUTES.APP.SETTINGS_EXPORT} replace />}
          />
          <Route
            path="settings/billing"
            element={<Navigate to={ROUTES.APP.SETTINGS_BILLING} replace />}
          />
          <Route
            path="settings/categories"
            element={<Navigate to={`${ROUTES.APP.CATEGORIES}?tab=manage`} replace />}
          />
          {MFA_ENABLED ? (
            <>
              <Route
                path="settings/security/2fa/enable"
                element={
                  <TotpPageShell>
                    <EnableTotpPage />
                  </TotpPageShell>
                }
              />
              <Route
                path="settings/security/2fa/disable"
                element={
                  <TotpPageShell>
                    <DisableTotpPage />
                  </TotpPageShell>
                }
              />
            </>
          ) : (
            <>
              <Route
                path="settings/security/2fa/enable"
                element={<Navigate to={ROUTES.APP.SETTINGS_SECURITY} replace />}
              />
              <Route
                path="settings/security/2fa/disable"
                element={<Navigate to={ROUTES.APP.SETTINGS_SECURITY} replace />}
              />
            </>
          )}
        </Route>
      </Routes>
    </>
  )
}
